import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Layouts
import ProtectedLayout from './components/ProtectedLayout';

// Auth Pages (eagerly loaded - needed for entry)
import Welcome from './pages/auth/Welcome';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';

// Main Pages (eagerly loaded - primary views)
import Home from './pages/Home';

// Lazy loaded pages
const Favorites = lazy(() => import('./pages/Favorites'));
const Nearby = lazy(() => import('./pages/Nearby'));
const Profile = lazy(() => import('./pages/Profile'));
const Scan = lazy(() => import('./pages/Scan'));
const Notifications = lazy(() => import('./pages/Notifications'));
const SpinWheel = lazy(() => import('./pages/SpinWheel'));
const EditProfile = lazy(() => import('./pages/EditProfile'));
const Friends = lazy(() => import('./pages/Friends'));
const SharedInbox = lazy(() => import('./pages/SharedInbox'));
const Points = lazy(() => import('./pages/Points'));
const Appearance = lazy(() => import('./pages/Appearance'));
const Inbox = lazy(() => import('./pages/Inbox'));
const Prizes = lazy(() => import('./pages/Prizes'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Subscription = lazy(() => import('./pages/Subscription'));
const SubscriptionCallback = lazy(() => import('./pages/SubscriptionCallback'));
const RestaurantDetail = lazy(() => import('./pages/restaurant/RestaurantDetail'));
const AdminStats = lazy(() => import('./pages/admin/AdminStats'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminRestaurants = lazy(() => import('./pages/admin/AdminRestaurants'));
const AdminPasswordRequests = lazy(() => import('./pages/admin/AdminPasswordRequests'));
const AdminPrizes = lazy(() => import('./pages/admin/AdminPrizes'));
const AdminCoupons = lazy(() => import('./pages/admin/AdminCoupons'));
const AdminBlog = lazy(() => import('./pages/admin/AdminBlog'));
const AdminNotifications = lazy(() => import('./pages/admin/AdminNotifications'));
const OwnerRestaurants = lazy(() => import('./pages/owner/OwnerRestaurants'));
const OwnerStats = lazy(() => import('./pages/owner/OwnerStats'));
const CreateRestaurant = lazy(() => import('./pages/owner/CreateRestaurant'));
const EditRestaurant = lazy(() => import('./pages/owner/EditRestaurant'));
const OwnerBranches = lazy(() => import('./pages/owner/OwnerBranches'));
const OwnerPosts = lazy(() => import('./pages/owner/OwnerPosts'));
const OwnerAliado = lazy(() => import('./pages/owner/OwnerAliado'));
const Help = lazy(() => import('./pages/Help'));
const Explore = lazy(() => import('./pages/Explore'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const MapPage = lazy(() => import('./pages/MapPage'));

import OfflineBanner from './components/OfflineBanner';

// Auth Guard for public routes
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Placeholder components for routes not yet fully implemented
const ComingSoon = ({ title }) => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6 pb-24">
    <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-4">
      <span className="text-2xl">🚧</span>
    </div>
    <h1 className="text-xl font-bold text-gray-800 mb-2">{title}</h1>
    <p className="text-gray-500 text-center">Esta sección estará disponible pronto</p>
  </div>
);

// Suspense fallback spinner
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const SmartHome = () => {
  const { user } = useAuth();
  if (user?.role === 'owner') return <Suspense fallback={<PageLoader />}><OwnerRestaurants /></Suspense>;
  return <Home />;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public Routes */}
      <Route path="/welcome" element={<PublicRoute><Welcome /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/explore" element={<Explore />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/help" element={<Help />} />
      <Route path="/restaurant/:id" element={<RestaurantDetail />} />
      <Route path="/map" element={<MapPage />} />

      {/* Protected Routes */}
      <Route element={<ProtectedLayout />}>
        <Route path="/" element={<SmartHome />} />
        <Route path="/favorites" element={<Favorites />} />
        <Route path="/nearby" element={<Nearby />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/scan" element={<Scan />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/spin-wheel" element={<SpinWheel />} />
        <Route path="/edit-profile" element={<EditProfile />} />
        <Route path="/friends" element={<Friends />} />
        <Route path="/shared-inbox" element={<SharedInbox />} />
        <Route path="/points" element={<Points />} />
        <Route path="/appearance" element={<Appearance />} />
        <Route path="/inbox" element={<Inbox />} />
        
        {/* Admin */}
        <Route path="/admin/stats" element={<AdminStats />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/restaurants" element={<AdminRestaurants />} />
        <Route path="/admin/password-requests" element={<AdminPasswordRequests />} />
        <Route path="/admin/prizes" element={<AdminPrizes />} />
        <Route path="/admin/coupons" element={<AdminCoupons />} />
        <Route path="/admin/blog" element={<AdminBlog />} />
        <Route path="/admin/notifications" element={<AdminNotifications />} />
        
        {/* Owner */}
        <Route path="/owner/my-restaurants" element={<OwnerRestaurants />} />
        <Route path="/owner/stats" element={<OwnerStats />} />
        <Route path="/owner/create-restaurant" element={<CreateRestaurant />} />
        <Route path="/owner/edit-restaurant/:id" element={<EditRestaurant />} />
        <Route path="/owner/branches/:id" element={<OwnerBranches />} />
        <Route path="/owner/posts" element={<OwnerPosts />} />
        <Route path="/owner/aliado" element={<OwnerAliado />} />
        
        {/* User */}
        <Route path="/prizes" element={<Prizes />} />
        <Route path="/subscription" element={<Subscription />} />
        <Route path="/subscription/callback" element={<SubscriptionCallback />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <div className="max-w-lg mx-auto bg-white dark:bg-gray-900 min-h-screen transition-colors duration-300">
            <OfflineBanner />
            <AppRoutes />
          </div>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
